import { useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { PageChoiceSection, SocialPage } from '../lib/pageChoice';

interface Props {
  /** Berekend door de parent met facebookPageSection/linkedinPageSection. */
  section: PageChoiceSection;
  pages: SocialPage[];
  currentPageId: string | null;
  currentPageName: string | null;
  saving: boolean;
  /** null = terug naar de default-optie (alleen mogelijk mét defaultOptionLabel). */
  onSelect: (pageId: string | null) => void;
  label: string;
  /**
   * Gevuld (LinkedIn: "Persoonlijk profiel"): permanente dropdown met deze
   * default als eerste, kiesbare optie. Leeg (Facebook): verplichte keuze
   * met disabled placeholder en een chosen-weergave met wijzig-knop.
   */
  defaultOptionLabel?: string;
}

export function PageChoice({
  section,
  pages,
  currentPageId,
  currentPageName,
  saving,
  onSelect,
  label,
  defaultOptionLabel,
}: Props) {
  const selectId = useId();
  const [changing, setChanging] = useState(false);
  if (section === 'hidden') return null;

  if (section === 'chosen' && !changing) {
    return (
      <div className="flex items-center gap-2 px-3 pb-3 text-sm">
        <span className="text-gray-600">Post op pagina:</span>
        <span className="font-medium text-gray-900">{currentPageName}</span>
        <button
          type="button"
          onClick={() => setChanging(true)}
          className="text-teal-700 hover:underline"
        >
          Wijzigen
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3">
      <label htmlFor={selectId} className="block text-sm text-gray-600 mb-1.5">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <select
          id={selectId}
          value={currentPageId ?? ''}
          disabled={saving}
          onChange={(e) => {
            if (e.target.value) {
              onSelect(e.target.value);
              setChanging(false);
            } else if (defaultOptionLabel) {
              onSelect(null);
              setChanging(false);
            }
          }}
          className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
        >
          {defaultOptionLabel ? (
            <option value="">{defaultOptionLabel}</option>
          ) : (
            <option value="" disabled>
              Kies een pagina...
            </option>
          )}
          {pages.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
      </div>
    </div>
  );
}
